/**
 * Structural Opening Tools — BIM Structure tab > Opening panel.
 *
 * Each tool creates a parametric void element (category 'Structural Openings')
 * that host renderers subtract from their solid via CSG. Flavors mirror BIM:
 * - By Face  : void hugging a picked host face (two clicks define footprint).
 * - Shaft    : vertical volume cutting through floors across several levels.
 * - Wall     : rectangular opening in a wall — sized along the wall run,
 *              positioned at the sill offset, spanning the wall thickness.
 * - Vertical : plumb void through floors/roofs.
 * - Cut      : hole through a framing member web.
 *
 * The two-click rectangle is defined on the working (ground) plane; flavors
 * that know their host refine the void into host-local dimensions.
 */

import { v4 as uuidv4 } from 'uuid';
import * as THREE from 'three';
import { ThreeEvent } from '@react-three/fiber';
import { BaseTool } from './BaseTool';
import { Element, StructuralOpeningElement, StructuralOpeningGeometryData, StructuralOpeningKind } from '../core/types';

/** Categories that can host an opening. */
export const STRUCTURAL_HOST_CATEGORIES = [
    'Walls', 'Floors', 'Roofs', 'Structural Framing', 'Structural Columns',
    'Structural Foundations', 'Ceilings', 'Structural In-Place',
];

/** Build the 4 corners of a rectangle between two points (shared preview). */
function rectPoints(a: THREE.Vector3, b: THREE.Vector3): THREE.Vector3[] {
    return [
        new THREE.Vector3(a.x, a.y, a.z),
        new THREE.Vector3(b.x, a.y, a.z),
        new THREE.Vector3(b.x, b.y, a.z),
        new THREE.Vector3(a.x, b.y, a.z),
    ];
}

/**
 * Shared base: two-click rectangle on the working plane, then a flavor-specific
 * `build` that turns the rectangle + optional host into a void element.
 */
abstract class TwoPointOpeningTool extends BaseTool {
    cursor = 'crosshair';
    abstract kind: StructuralOpeningKind;

    private firstPoint: THREE.Vector3 | null = null;
    private pendingHost: Element | null = null;

    /** Host element under the cursor, if the flavor needs one. */
    protected pickHost(event: ThreeEvent<PointerEvent>, allowedCategories?: string[]): Element | null {
        if (!this.context) return null;
        const hit = this.raycastSceneAtEvent(event).find(i => {
            if (!i.face) return false;
            const id = this.findId(i.object);
            const el = id ? this.context!.bimStore.project.elements.get(id) : null;
            return el && (allowedCategories ? allowedCategories.includes(el.category) : STRUCTURAL_HOST_CATEGORIES.includes(el.category));
        });
        if (!hit) return null;
        const id = this.findId(hit.object)!;
        return this.context.bimStore.project.elements.get(id) ?? null;
    }

    protected abstract build(
        center: THREE.Vector3,
        footprint: { w: number; h: number },
        host: Element | null,
    ): StructuralOpeningGeometryData;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const host = this.pickHost(event);
        const planePoint = this.getIntersection(event);
        if (!planePoint) return;
        const snapped = this.snapToGrid(planePoint);

        if (!this.firstPoint) {
            this.firstPoint = snapped;
            this.pendingHost = host;
            return;
        }

        const start = this.firstPoint;
        const hostEl = this.pendingHost ?? host;
        this.firstPoint = null;
        this.pendingHost = null;

        const center = new THREE.Vector3(
            (start.x + snapped.x) / 2,
            (start.y + snapped.y) / 2,
            Math.max(start.z, snapped.z),
        );
        const w = Math.abs(snapped.x - start.x);
        const h = Math.abs(snapped.y - start.y);

        const geometryData = this.build(center, { w, h }, hostEl);
        const element: StructuralOpeningElement = {
            id: uuidv4(),
            category: 'Structural Openings',
            type: `${this.kind} Opening`,
            parameters: {
                'Opening Kind': this.kind,
                'Width': `${(geometryData.width * 1000).toFixed(0)} mm`,
                'Height': `${(geometryData.height * 1000).toFixed(0)} mm`,
                'Depth': `${(geometryData.depth * 1000).toFixed(0)} mm`,
                'Host': hostEl ? `${hostEl.category} (${hostEl.type})` : 'None',
            },
            constraints: geometryData.hostId
                ? [{ type: 'HOST_CONTAINED' as any, targetId: geometryData.hostId, data: {} }]
                : [],
            geometryData,
        };

        this.context.bimStore.addElement(element);
        this.context.appStore.setToolPreview(null);
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (!point) return;
        const snapped = this.snapToGrid(point);
        if (this.firstPoint) {
            this.context.appStore.setToolPreview({
                type: 'polygon',
                points: rectPoints(this.firstPoint, snapped),
                color: '#ff6b6b',
            });
        } else {
            this.context.appStore.setToolPreview(null);
        }
    }

    onPointerUp(): void {}

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            this.firstPoint = null;
            this.pendingHost = null;
            this.context?.appStore.setToolPreview(null);
        } else {
            super.onKeyDown(event);
        }
    }

    /** Default rectangular void size from tool properties (meters). */
    protected defaultRect(): { w: number; h: number } {
        const props = this.context?.bimStore.toolProperties ?? {};
        return {
            w: (props.openingWidth ?? 600) / 1000,
            h: (props.openingHeight ?? 600) / 1000,
        };
    }
}

/** By Face: flat void hugging a picked host face. */
export class OpeningByFaceTool extends TwoPointOpeningTool {
    id = 'opening_by_face';
    name = 'Opening By Face';
    kind: StructuralOpeningKind = 'ByFace';

    protected build(center: THREE.Vector3, footprint: { w: number; h: number }, host: Element | null): StructuralOpeningGeometryData {
        const def = this.defaultRect();
        return {
            kind: 'ByFace',
            hostId: host?.id,
            center: [center.x, center.y, center.z],
            width: Math.max(footprint.w, def.w),
            height: Math.max(footprint.h, def.h),
            depth: 0.4,
            rotation: 0,
        };
    }
}

/** Wall Opening: void through a wall — along-wall size, sill offset, full thickness. */
export class OpeningWallTool extends TwoPointOpeningTool {
    id = 'opening_wall';
    name = 'Wall Opening';
    kind: StructuralOpeningKind = 'Wall';

    protected build(center: THREE.Vector3, footprint: { w: number; h: number }, host: Element | null): StructuralOpeningGeometryData {
        const def = this.defaultRect();
        const props = this.context?.bimStore.toolProperties ?? {};
        const sill = (props.openingSillOffset ?? 900) / 1000;
        const openH = Math.max(footprint.h, def.h);

        const wallGeom = host?.category === 'Walls' ? (host.geometryData as any) : null;
        if (wallGeom?.start && wallGeom?.end) {
            const start = new THREE.Vector3(...wallGeom.start);
            const end = new THREE.Vector3(...wallGeom.end);
            const angle = Math.atan2(end.y - start.y, end.x - start.x);
            const baseZ = wallGeom.baseOffset || 0;
            // Along-wall size from the click footprint projected on the wall axis.
            const along = Math.max(
                Math.abs((footprint.w) * Math.cos(angle)) + Math.abs((footprint.h) * Math.sin(angle)),
                def.w,
            );
            return {
                kind: 'Wall',
                hostId: host!.id,
                center: [center.x, center.y, baseZ + sill + openH / 2],
                width: along,           // along wall run
                height: openH,          // vertical
                depth: (wallGeom.thickness ?? 0.2) + 0.5, // pierce across the wall
                rotation: THREE.MathUtils.radToDeg(angle),
            };
        }

        // No wall under the cursor: axis-aligned vertical prism at click point.
        return {
            kind: 'Wall',
            hostId: host?.id,
            center: [center.x, center.y, sill + openH / 2],
            width: Math.max(footprint.w, def.w),
            height: openH,
            depth: 0.6,
            rotation: 0,
        };
    }
}

/** Vertical Opening: plumb void through floors/roofs. */
export class OpeningVerticalTool extends TwoPointOpeningTool {
    id = 'opening_vertical';
    name = 'Vertical Opening';
    kind: StructuralOpeningKind = 'Vertical';

    protected build(center: THREE.Vector3, footprint: { w: number; h: number }, host: Element | null): StructuralOpeningGeometryData {
        const def = this.defaultRect();
        // Tall prism: 3m vertical pierce covers a typical floor stack.
        return {
            kind: 'Vertical',
            hostId: host?.id,
            center: [center.x, center.y, center.z + 1.5],
            width: Math.max(footprint.w, def.w),
            height: Math.max(footprint.h, def.h),
            depth: 3.0,
            rotation: 0,
        };
    }
}

/** Cut Opening: hole through a framing member. */
export class OpeningCutTool extends TwoPointOpeningTool {
    id = 'opening_cut';
    name = 'Cut Opening';
    kind: StructuralOpeningKind = 'Cut';

    protected build(center: THREE.Vector3, footprint: { w: number; h: number }, host: Element | null): StructuralOpeningGeometryData {
        const def = this.defaultRect();
        const w = Math.max(Math.min(footprint.w, footprint.h) || def.w, 0.1);
        const h = Math.max(Math.min(footprint.w, footprint.h) || def.h, 0.1);
        // Pierce along Z through the member (soffit-to-top clearance box).
        return {
            kind: 'Cut',
            hostId: host?.id,
            center: [center.x, center.y, center.z],
            width: w,
            height: h,
            depth: 1.2,
            rotation: 0,
        };
    }
}

/** Shaft Opening: one-click-start/second-click-size vertical shaft spanning levels. */
export class OpeningShaftTool extends BaseTool {
    id = 'opening_shaft';
    name = 'Shaft Opening';
    cursor = 'crosshair';

    private firstPoint: THREE.Vector3 | null = null;

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;
        const snapped = this.snapToGrid(point);

        if (!this.firstPoint) {
            this.firstPoint = snapped;
            return;
        }

        const start = this.firstPoint;
        this.firstPoint = null;
        const props = this.context.bimStore.toolProperties;
        const levels = props.shaftDepthLevels ?? 3;
        const levelHeight = 3.0;

        const w = Math.max(Math.abs(snapped.x - start.x), 0.5);
        const h = Math.max(Math.abs(snapped.y - start.y), 0.5);
        const depth = levelHeight * levels;

        const element: StructuralOpeningElement = {
            id: uuidv4(),
            category: 'Structural Openings',
            type: 'Shaft Opening',
            parameters: {
                'Opening Kind': 'Shaft',
                'Width': `${(w * 1000).toFixed(0)} mm`,
                'Length': `${(h * 1000).toFixed(0)} mm`,
                'Levels': levels,
                'Base Constraint': 'Level 1',
            },
            constraints: [],
            geometryData: {
                kind: 'Shaft',
                center: [(start.x + snapped.x) / 2, (start.y + snapped.y) / 2, depth / 2],
                width: w,
                height: h,
                depth,
                rotation: 0,
                levelsThrough: levels,
            },
        };

        this.context.bimStore.addElement(element);
        this.context.appStore.setToolPreview(null);
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point && this.firstPoint) {
            this.context.appStore.setToolPreview({
                type: 'polygon',
                points: rectPoints(this.firstPoint, this.snapToGrid(point)),
                color: '#ff6b6b',
            });
        } else if (this.firstPoint) {
            this.context.appStore.setToolPreview({
                type: 'polygon',
                points: rectPoints(this.firstPoint, this.firstPoint),
                color: '#ff6b6b',
            });
        }
    }

    onPointerUp(): void {}

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            this.firstPoint = null;
            this.context?.appStore.setToolPreview(null);
        } else {
            super.onKeyDown(event);
        }
    }
}
